/**
 * Text manipulation utilities
 */

/**
 * Generate a unique ID with optional prefix.
 * Uses timestamp + random string for uniqueness.
 */
export const generateId = (prefix: string = 'id'): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `${prefix}-${timestamp}-${random}`;
};

/**
 * Capitalize each word in a string.
 */
const capitalizeWords = (str: string): string => {
    if (!str) {
        return '';
    }
    return str
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Get initials from a name (first letter of each word).
 */
export const getInitials = (name: string, maxLength: number = 2): string => {
    if (!name) {
        return '';
    }
    return name
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .slice(0, maxLength)
        .join('');
};

/**
 * Sanitize full name for storage.
 * Trims whitespace and capitalizes each word.
 */
export const sanitizeFullName = (name: string): string => {
    return capitalizeWords(name);
};

/**
 * Portuguese prepositions to keep lowercase in city/proper names
 */
const PORTUGUESE_PREPOSITIONS = ['de', 'da', 'do', 'das', 'dos', 'e'];

/**
 * Sanitize city name for storage.
 * Trims whitespace and capitalizes each word, keeping prepositions lowercase.
 */
export const sanitizeCity = (city: string): string => {
    if (!city) {
        return '';
    }
    return city
        .trim()
        .split(/\s+/)
        .map((word) => {
            const lowerWord = word.toLowerCase();
            if (PORTUGUESE_PREPOSITIONS.includes(lowerWord)) {
                return lowerWord;
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
};
